import { auth } from './firebase';
import type { DiligenceTemplateSummary } from '../types';
import { getTemplateUsability, normalizeTemplateStatus } from '../utils/documentUiState';
import {
  getDiligenceUploadErrorMessage,
  getUnsupportedDiligenceFormatMessage
} from '../utils/diligenceUploadErrors';
import { uploadDiligenceFile } from './diligenceUploadService';
import {
  createDiligenceUploadHttpFailure,
  DiligenceUploadFailure,
  type DiligenceUploadStage
} from './diligenceUploadFailure';

const TEMPLATES_ENDPOINT = '/api/diligence-templates';
const MAX_TEMPLATE_BYTES = 20 * 1024 * 1024;
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf', doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

const request = async <T = void>(
  method: 'POST' | 'PATCH' | 'DELETE',
  body: Record<string, unknown>,
  stage: DiligenceUploadStage = 'finalize'
): Promise<T> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para gestionar plantillas.');
  const token = await user.getIdToken();
  const response = await fetch(TEMPLATES_ENDPOINT, {
    method,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!response.ok) {
    const failure = await createDiligenceUploadHttpFailure(response, stage, 'No se pudo completar la operación con la plantilla.');
    throw new DiligenceUploadFailure(
      getDiligenceUploadErrorMessage('template', failure.errorKind),
      { ...failure, stage, cause: failure }
    );
  }
  const payload = await response.json().catch(() => null) as T | null;
  return payload as T;
};

export const listDiligenceTemplates = async (): Promise<DiligenceTemplateSummary[]> => {
  return (await fetchDiligenceTemplates()).templates;
};

export const listDiligenceTemplatePage = (after?: string) => fetchDiligenceTemplates(
  `?${new URLSearchParams({ pageSize: '20', ...(after ? { after } : {}) })}`
);

async function fetchDiligenceTemplates(query = ''): Promise<{ templates: DiligenceTemplateSummary[]; nextCursor: string | null }> {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para consultar plantillas.');
  const token = await user.getIdToken();
  const response = await fetch(`${TEMPLATES_ENDPOINT}${query}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error('No se pudo cargar la biblioteca.');
  const payload = await response.json() as { templates?: Array<Record<string, unknown>>; nextCursor?: string | null } | Array<Record<string, unknown>>;
  const templates = Array.isArray(payload) ? payload : payload.templates || [];
  return { nextCursor: !Array.isArray(payload) && typeof payload.nextCursor === 'string' ? payload.nextCursor : null,
    templates: templates.map((template) => {
    const status = normalizeTemplateStatus(template.status);
    return ({
    id: String(template.id || template.templateId || ''),
    name: String(template.displayName || template.name || 'Plantilla sin nombre'),
    description: typeof template.description === 'string' ? template.description : '',
    userInstructions: typeof template.userInstructions === 'string' ? template.userInstructions : '',
    sourceFormat: template.sourceFormat === 'pdf' || template.sourceFormat === 'doc' ? template.sourceFormat : 'docx',
    status,
    usability: getTemplateUsability(status, template.usability),
    statusReason: typeof template.statusReason === 'string'
      ? template.statusReason
      : typeof template.reviewReason === 'string'
        ? template.reviewReason
        : undefined,
    processingStartedAt: typeof template.processingStartedAt === 'string'
      ? template.processingStartedAt
      : undefined,
    updatedAt: typeof template.updatedAt === 'string' ? template.updatedAt : undefined
    });
  }) };
}

export const uploadDiligenceTemplate = async (
  file: File,
  displayName: string,
  description: string,
  templateId?: string,
  userInstructions = ''
) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para guardar una plantilla.');
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const unsupportedFormatMessage = getUnsupportedDiligenceFormatMessage(file.name);
  if (unsupportedFormatMessage) {
    throw new DiligenceUploadFailure(unsupportedFormatMessage, {
      stage: 'prepare', errorKind: 'unsupported_file_format'
    });
  }
  if (file.size > MAX_TEMPLATE_BYTES) {
    throw new DiligenceUploadFailure('La plantilla no puede superar 20 MiB.', {
      stage: 'prepare', errorKind: 'file_too_large'
    });
  }

  const contentType = MIME_BY_EXTENSION[extension];
  const prepared = await request<{ templateId: string; versionId: string; uploadUrl: string; storagePath: string; fileName: string; contentType: string }>('POST', {
    action: 'prepareUpload', displayName, description, userInstructions, templateId,
    originalFileName: file.name, contentType, size: file.size
  });
  await uploadDiligenceFile(prepared.uploadUrl, file, prepared.contentType);
  await request('POST', { action: 'finalize', templateId: prepared.templateId, versionId: prepared.versionId, storagePath: prepared.storagePath, fileName: prepared.fileName, contentType: prepared.contentType, size: file.size, displayName, description, userInstructions }, 'finalize');
};

export const getDiligenceTemplateDownloadUrl = async (templateId: string): Promise<string> => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para descargar plantillas.');
  const token = await user.getIdToken();
  const query = new URLSearchParams({ action: 'download', templateId });
  const response = await fetch(`${TEMPLATES_ENDPOINT}?${query}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!response.ok) throw new Error('No se pudo descargar el documento original. Inténtalo de nuevo.');
  const payload = await response.json() as { downloadUrl: string };
  return payload.downloadUrl;
};

export const updateDiligenceTemplate = (
  templateId: string,
  displayName: string,
  description: string,
  userInstructions = ''
) => (
  request('PATCH', { templateId, displayName, description, userInstructions })
);

export const deleteDiligenceTemplate = (templateId: string) => request('DELETE', { templateId });

export const retryDiligenceTemplateProcessing = (templateId: string) => (
  request<{ templateId: string; versionId: string; status: 'processing' }>('POST', {
    action: 'retryProcessing', templateId
  })
);

export const deleteAllDiligenceTemplates = () => (
  request<{ ok: true; deletedCount: number }>('DELETE', { action: 'deleteAll' })
);
